import React, { useEffect, useRef, memo, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ErrorCloseBtnIcon from "../../assets/images/Error_Close_btn.svg";
import warningIcon from "../../assets/images/warningIcon.png";
// import tickIcon from "../../assets/images/tick2.png"; // Currently unused but kept for future use
import './ErrorComponent.scss';

// Custom hook for handling outside clicks - optimized with useCallback
function useOutsideAlerter(ref, showErrorMessage, closeError) {
  const handleClickOutside = useCallback((event) => {
    if (ref.current && !ref.current.contains(event.target) && showErrorMessage) {
      closeError();
    }
  }, [ref, showErrorMessage, closeError]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);
}

const ErrorComponent = memo(({ showError, closeError, apiError, errorCode }) => {
  const { showErrorMessage } = useSelector(({ errorReducer: { showErrorMessage } }) => ({ showErrorMessage }));
  const wrapperRef = useRef(null);
  
  useOutsideAlerter(wrapperRef, showErrorMessage, closeError);

  // Memoized error color code calculation
  const errorColorCode = useMemo(() => {
    switch (errorCode) {
      case 1100:
        return 'Error1100';
      case 1020:
        return 'Error1020';
      case 1:
        return 'Error1';
      case 1013:
        return 'Error1013';
      case 1022:
      case 1021:
        return 'Error1021';
      case 1500:
      case 1501:
        return 'Error1500';
      case 'cancelled':
        return 'ErrorCancelled';
      case 200:
        return 'Success200';
      default:
        return 'defaultError';
    }
  }, [errorCode]);

  // Memoized conditional content
  const isSuccess = useMemo(() => errorCode === 200, [errorCode]);
  const isCancelled = useMemo(() => errorCode === 'cancelled', [errorCode]);

  // Memoized icon content
  const iconContent = useMemo(() => {
    if (isSuccess) {
      return (
        <span>
          {/* <img src={tickIcon} className="img-responsive" alt="Success" width="70px" height="70px" /> */}
        </span>
      );
    }
    return (
      <img 
        src={warningIcon} 
        className="img-responsive" 
        alt="Warning" 
        width="70px" 
        height="70px" 
      />
    );
  }, [isSuccess]);

  // Memoized close button content
  const closeButtonContent = useMemo(() => {
    return isCancelled ? 'X' : <img src={ErrorCloseBtnIcon} alt="Close Error" />;
  }, [isCancelled]);

  // Memoized header text
  const headerText = useMemo(() => {
    return isSuccess ? 'Success' : 'Error';
  }, [isSuccess]);

  // Early return for better performance
  if (!showError) {
    return null;
  }

  return (
    <div className={`error-container ${errorColorCode}`} ref={wrapperRef}>
      <div className="row">
        <div className="col-sm-2 col-lg-1 d-flex justify-content-center">
          {iconContent}
        </div>
        <div className="col-sm-10 col-lg-11">
          <button onClick={closeError} type="button" aria-label="Close error">
            {closeButtonContent}
          </button>
          <h4>{headerText}</h4>
          <p>{apiError}</p>
        </div>
      </div>
    </div>
  );
});

// Display name for debugging
ErrorComponent.displayName = 'ErrorComponent';

// PropTypes for type checking and documentation
ErrorComponent.propTypes = {
  showError: PropTypes.bool.isRequired,
  closeError: PropTypes.func.isRequired,
  apiError: PropTypes.string,
  errorCode: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ])
};

// Default props
ErrorComponent.defaultProps = {
  apiError: '',
  errorCode: null
};

export default ErrorComponent;